/**
 * The Enrollments page's state, owned by the URL: the view, the roster's
 * search and filters, a deep link's pinned student and the class it focuses,
 * and the way back. One owner, so Back, a tab round-trip and a link all read
 * the same thing.
 *
 *   ?view=due                   the Payments due tab; absent is the roster
 *   ?search= ?status= ?level= ?class= ?billing=   roster filters
 *   ?sort=newest|oldest|name    the roster's order; absent is chase order
 *   ?student= | ?enrollmentId=  the record open in the drawer, over the roster
 *   ?classCodeId=               the class the open record focuses
 *   ?returnTo=                  the validated origin of the back link
 *
 * Typing replaces the history entry; every other change pushes one, so Back
 * steps through filter sets and tabs the way the admin made them.
 */
import { type RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  ENROLLMENT_ROSTER_BILLING_FILTERS,
  ENROLLMENT_ROSTER_SORTS,
  ENROLLMENT_ROSTER_STATUS_FILTERS,
  type EnrollmentRosterBillingFilter,
  type EnrollmentRosterSort,
  type EnrollmentRosterStatusFilter,
} from '@shared/enrollmentRoster';
import { getAllInternalLevels } from '@/utils/levelMapping';

export type RosterView = 'enrollments' | 'due';

export interface RosterUrlState {
  view: RosterView;
  search: string;
  status: EnrollmentRosterStatusFilter;
  /** '' is every level. */
  level: string;
  /** The Class filter; '' is every class. */
  classCodeId: string;
  /** '' is every money state. */
  billing: EnrollmentRosterBillingFilter | '';
  /** The roster's order; chase order when absent. */
  sort: EnrollmentRosterSort;
  /** The open record's billing participant. */
  student: string | null;
  /** The open record's enrollment, when a link named one instead. */
  enrollmentId: string | null;
  /** The class the open record focuses, from a class link. */
  focusClassCodeId: string | null;
  returnTo: string | null;
}

const OBJECT_ID = /^[a-f\d]{24}$/i;
const PARTICIPANT_ID = /^(enrollment:)?[a-f\d]{24}$/i;

export function readRosterUrlState(params: URLSearchParams): RosterUrlState {
  const view = params.get('view');
  const status = params.get('status');
  const level = params.get('level') ?? '';
  const billing = params.get('billing');
  const classCodeId = params.get('class') ?? '';
  const student = params.get('student');
  const enrollmentId = params.get('enrollmentId');
  const focus = params.get('classCodeId');
  const sort = params.get('sort');
  return {
    view: view === 'due' ? view : 'enrollments',
    search: (params.get('search') ?? '').slice(0, 200),
    status: (ENROLLMENT_ROSTER_STATUS_FILTERS as readonly string[]).includes(
      status ?? ''
    )
      ? (status as EnrollmentRosterStatusFilter)
      : 'current',
    level: getAllInternalLevels().includes(level) ? level : '',
    classCodeId: OBJECT_ID.test(classCodeId) ? classCodeId : '',
    billing: (ENROLLMENT_ROSTER_BILLING_FILTERS as readonly string[]).includes(
      billing ?? ''
    )
      ? (billing as EnrollmentRosterBillingFilter)
      : '',
    sort: (ENROLLMENT_ROSTER_SORTS as readonly string[]).includes(sort ?? '')
      ? (sort as EnrollmentRosterSort)
      : 'chase',
    student: student && PARTICIPANT_ID.test(student) ? student : null,
    enrollmentId:
      !student && enrollmentId && OBJECT_ID.test(enrollmentId)
        ? enrollmentId
        : null,
    focusClassCodeId: focus && OBJECT_ID.test(focus) ? focus : null,
    returnTo: params.get('returnTo'),
  };
}

/**
 * The query that reads back as `state`, defaults left out: the inverse of
 * `readRosterUrlState`. `returnTo` is the caller's to validate and add.
 */
export function rosterUrlQuery(state: RosterUrlState): URLSearchParams {
  const query = new URLSearchParams();
  if (state.view !== 'enrollments') query.set('view', state.view);
  if (state.search) query.set('search', state.search);
  if (state.status !== 'current') query.set('status', state.status);
  if (state.level) query.set('level', state.level);
  if (state.classCodeId) query.set('class', state.classCodeId);
  if (state.billing) query.set('billing', state.billing);
  if (state.sort !== 'chase') query.set('sort', state.sort);
  if (state.student) query.set('student', state.student);
  else if (state.enrollmentId) query.set('enrollmentId', state.enrollmentId);
  if (state.focusClassCodeId) query.set('classCodeId', state.focusClassCodeId);
  return query;
}

/** The filters that narrow the roster: what "any filter" means. */
export function hasRosterFilters(state: RosterUrlState): boolean {
  return Boolean(
    state.search.trim() ||
      state.status !== 'current' ||
      state.level ||
      state.classCodeId ||
      state.billing
  );
}

type Patch = Partial<Record<string, string | null>>;

/**
 * A control that takes itself away when it writes the URL (Clear filters)
 * hands focus to `target` once the new URL is on screen, never to the page
 * body (C7). Call the returned function just before the write.
 */
export function useFocusAfterUrl(state: RosterUrlState) {
  const pending = useRef<RefObject<HTMLElement | null> | null>(null);
  useEffect(() => {
    const target = pending.current;
    pending.current = null;
    target?.current?.focus();
  }, [state]);
  return useCallback((target: RefObject<HTMLElement | null>) => {
    pending.current = target;
  }, []);
}

export function useRosterUrlState() {
  const [params, setParams] = useSearchParams();
  const state = useMemo(() => readRosterUrlState(params), [params]);
  /**
   * Write some params; `null` or '' removes one. `replace` for typing, a push
   * for everything else. `state` rides on the history entry (the drawer marks
   * the entry a row click pushed, so Back can be told from a deep link).
   */
  const update = useCallback(
    (
      patch: Patch,
      { replace = false, state }: { replace?: boolean; state?: unknown } = {}
    ) =>
      setParams(
        previous => {
          const next = new URLSearchParams(previous);
          for (const [key, value] of Object.entries(patch)) {
            if (value) next.set(key, value);
            else next.delete(key);
          }
          return next;
        },
        { replace, state }
      ),
    [setParams]
  );
  return { state, params, update };
}
